'use client';

import { useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

import LocationSelector from './LocationSelector';
import LanguageVoiceToggle from './LanguageVoiceToggle';

interface HeaderProps {
  userRole?: 'consumer' | 'admin';
  isAuthenticated?: boolean;
}

const Header = ({ userRole = 'consumer', isAuthenticated = false }: HeaderProps) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeNav, setActiveNav] = useState('/home-dashboard');

  const consumerNavItems = [
    { label: 'Home', path: '/home-dashboard', icon: 'HomeIcon' },
    { label: 'Products', path: '/product-listing', icon: 'ShoppingBagIcon' },
    { label: 'Pricing', path: '/fish-price-discovery', icon: 'CurrencyDollarIcon' },
    { label: 'Cart', path: '/shopping-cart', icon: 'ShoppingCartIcon' },
    { label: 'Orders', path: '/order-tracking', icon: 'ClipboardDocumentListIcon' },
  ];

  const adminNavItems = [
    { label: 'Dashboard', path: '/admin-dashboard', icon: 'ChartBarIcon' },
    { label: 'Orders', path: '/order-management', icon: 'ClipboardDocumentCheckIcon' },
    { label: 'Products', path: '/product-management', icon: 'CubeIcon' },
  ];

  const navigationItems = userRole === 'admin' ? adminNavItems : consumerNavItems;

  const handleNavClick = (path: string) => {
    setActiveNav(path);
    setIsMobileMenuOpen(false);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-[100] bg-card shadow-elevation-md">
      <div className="flex items-center justify-between h-[60px] px-6">
        {/* Logo Section */}
        <Link 
          href={userRole === 'admin' ? '/admin-dashboard' : '/home-dashboard'}
          className="flex items-center gap-3 transition-smooth hover:opacity-80"
          onClick={() => handleNavClick(userRole === 'admin' ? '/admin-dashboard' : '/home-dashboard')}
        >
          <div className="flex items-center justify-center w-10 h-10 bg-primary rounded-md">
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M16 4C16 4 8 8 8 16C8 20 10 24 16 28C22 24 24 20 24 16C24 8 16 4 16 4Z" fill="white" opacity="0.9"/>
              <ellipse cx="16" cy="14" rx="3" ry="2" fill="white"/>
              <path d="M13 18C13 18 14 20 16 20C18 20 19 18 19 18" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </div>
          <div className="flex flex-col">
            <span className="font-heading font-bold text-xl text-primary leading-none">FishBite</span>
            <span className="font-caption text-xs text-muted-foreground leading-none mt-0.5">
              {userRole === 'admin' ? 'Admin Portal' : 'Fresh Seafood Market'}
            </span>
          </div>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center gap-2">
          {navigationItems.map((item) => (
            <Link
              key={item.path}
              href={item.path}
              onClick={() => handleNavClick(item.path)}
              className={`flex items-center gap-2 px-4 py-2 rounded-md transition-smooth font-medium text-sm ${
                activeNav === item.path
                  ? 'bg-primary text-primary-foreground shadow-elevation-sm'
                  : 'text-foreground hover:bg-muted'
              }`}
            >
              <Icon name={item.icon as any} size={18} />
              <span>{item.label}</span>
            </Link>
          ))}
        </nav>

        {/* Right Section */}
        <div className="flex items-center gap-4">
          {/* Location & Language (Desktop) */}
          <div className="hidden lg:flex items-center gap-3">
            <LocationSelector />
            <LanguageVoiceToggle />
          </div>

          {/* User Account */}
          {isAuthenticated ? (
            <Link
              href="/user-profile"
              className="flex items-center gap-2 px-4 py-2 rounded-md transition-smooth hover:bg-muted"
              onClick={() => handleNavClick('/user-profile')}
            >
              <Icon name="UserCircleIcon" size={24} />
              <span className="hidden md:inline text-sm font-medium">Account</span>
            </Link>
          ) : (
            <Link
              href="/login"
              className="px-6 py-2 bg-primary text-primary-foreground rounded-md font-medium text-sm transition-smooth hover:shadow-elevation-md hover-lift"
            >
              Login
            </Link>
          )}

          {/* Mobile Menu Toggle */}
          <button
            onClick={toggleMobileMenu}
            className="lg:hidden p-2 rounded-md hover:bg-muted transition-smooth"
            aria-label="Toggle mobile menu"
          >
            <Icon name={isMobileMenuOpen ? 'XMarkIcon' : 'Bars3Icon'} size={24} />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-card border-t border-border animate-slide-down">
          <nav className="flex flex-col p-4 gap-2">
            {navigationItems.map((item) => (
              <Link
                key={item.path}
                href={item.path}
                onClick={() => handleNavClick(item.path)}
                className={`flex items-center gap-3 px-4 py-3 rounded-md transition-smooth ${
                  activeNav === item.path
                    ? 'bg-primary text-primary-foreground'
                    : 'text-foreground hover:bg-muted'
                }`}
              >
                <Icon name={item.icon as any} size={20} />
                <span className="font-medium">{item.label}</span>
              </Link>
            ))}
            
            {/* Mobile Location & Language */}
            <div className="flex flex-col gap-3 mt-4 pt-4 border-t border-border">
              <LocationSelector />
              <LanguageVoiceToggle />
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;